import { HttpErrorResponse } from '@angular/common/http';

/** Mensaje con el que requireAuth de la API rechaza un token vencido o invalido (403). */
export const MENSAJE_TOKEN_RECHAZADO = 'Forbidden for token';

/**
 * Lee el mensaje que manda la API en un error. El cuerpo puede venir como objeto
 * ({ message }), como arreglo ([{ message }], el formato de requireAuth), como texto
 * sin parsear o como Blob (las descargas piden responseType 'blob' y el cuerpo del
 * error tambien llega asi). Devuelve '' si no hay mensaje legible.
 */
export async function leerMensajeApi(error: HttpErrorResponse): Promise<string> {
  const cuerpo = await leerCuerpoApi(error);
  return typeof cuerpo?.message === 'string' ? cuerpo.message : '';
}

/** El cuerpo del error ya parseado (ver leerMensajeApi), o null si no se puede leer. */
async function leerCuerpoApi(error: HttpErrorResponse): Promise<any> {
  const cuerpo = await leerCuerpoCrudo(error);
  return (Array.isArray(cuerpo) ? cuerpo[0] : cuerpo) ?? null;
}

async function leerCuerpoCrudo(error: HttpErrorResponse): Promise<any> {
  let cuerpo: any = error?.error;
  try {
    if (cuerpo instanceof Blob) cuerpo = await cuerpo.text();
    if (typeof cuerpo === 'string') cuerpo = JSON.parse(cuerpo);
  } catch {
    return null;
  }
  return cuerpo ?? null;
}

/** Textos de la API que son de diagnóstico y no le dicen nada al asesor. */
const MENSAJE_TECNICO = /24ch|token/i;

/**
 * El motivo de un rechazo 4xx para mostrárselo al usuario, o '' si la API no mandó
 * uno pensado para él. Se descartan los arreglos de validateSchema (los mensajes de
 * Zod vienen en inglés: 'Required', 'Expected string, received null') y los textos
 * de diagnóstico ('Moneda incorrecta, 24ch', 'Token no válido'): ahí va el genérico.
 */
export async function leerMotivoParaUsuario(error: HttpErrorResponse): Promise<string> {
  if (!(error?.status >= 400 && error?.status < 500)) return '';
  const cuerpo = await leerCuerpoCrudo(error);
  if (!cuerpo || Array.isArray(cuerpo) || typeof cuerpo.message !== 'string') return '';
  return MENSAJE_TECNICO.test(cuerpo.message) ? '' : cuerpo.message;
}

/**
 * true si el 429 no es del limitador de consultas sino de la cola de PDF llena
 * (errorCode PDF_QUEUE_FULL, o el mensaje 'PDF queue ... full' del generador).
 * No es culpa del usuario y se le explica distinto.
 */
export async function esColaPdfLlena(error: HttpErrorResponse): Promise<boolean> {
  if (error?.status !== 429) return false;
  const cuerpo = await leerCuerpoApi(error);
  if (cuerpo?.errorCode === 'PDF_QUEUE_FULL' || cuerpo?.code === 'PDF_QUEUE_FULL') return true;
  return typeof cuerpo?.message === 'string' && /pdf queue/i.test(cuerpo.message);
}

/**
 * true si el 409 es el freno de la API para imprimir un borrador (code
 * CONTRATO_NO_EMITIDO): la póliza sigue en CREADO y hay que emitirla primero.
 */
export async function esContratoNoEmitido(error: HttpErrorResponse): Promise<boolean> {
  if (error?.status !== 409) return false;
  const cuerpo = await leerCuerpoApi(error);
  return cuerpo?.code === 'CONTRATO_NO_EMITIDO';
}

/**
 * true si el error dice que la sesion ya no sirve: un 401 (no llego token) o el 403
 * de requireAuth con 'Forbidden for token' (token vencido a las 24 h). Los demas 403
 * son de permisos ("No autorizado para ...") y no cierran la sesion.
 */
export async function esSesionVencida(error: HttpErrorResponse): Promise<boolean> {
  if (error?.status === 401) return true;
  if (error?.status !== 403) return false;
  return (await leerMensajeApi(error)) === MENSAJE_TOKEN_RECHAZADO;
}

/**
 * Espera maxima de un guardado (crear o editar un contrato o una solicitud, emitir).
 * Antes no habia ninguna: con el enlace de la tienda colgado la capa "Emitiendo
 * contrato, por favor espere" quedaba en pantalla hasta que el usuario recargaba.
 */
export const TIEMPO_MAXIMO_GUARDADO_MS = 120000;

/**
 * true si el pedido no tuvo respuesta (conexion cortada o tope de espera vencido). En
 * un guardado eso NO dice que no se guardo: el servidor pudo haberlo hecho igual.
 */
export function sinRespuestaDelServidor(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}
